/* eslint-disable @typescript-eslint/no-explicit-any */
import { DefaultOptions, QueryClient, UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { AxiosError } from 'axios';
import _ from 'lodash';

import { addSnackbarAlert } from '@sternum/ui';

import { i18n, namespaces } from '../i18n';
import { trpc, TRPCReactQueryError } from '../trpc';

const onError = _.throttle(
  (e: unknown) => {
    const err = e as TRPCReactQueryError;

    if (err.data?.code === 'UNAUTHORIZED') {
      addSnackbarAlert({
        variant: 'error',
        message: i18n.t('unauthorizedError', { ns: namespaces.common }) as string,
      });

      setTimeout(() => {
        // setTimeout - let execute every useEffect's to display correct error message if needed
        queryClient.invalidateQueries(getQueryKey(trpc.auth.me));
      }, 0);
    } else {
      console.error(err);
    }
  },
  1000,
  { leading: true, trailing: false },
);

const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    retry: false,
    onError(e) {
      onError(e);
    },
  },
};

export const queryClient = new QueryClient({ defaultOptions: queryConfig });

export type ExtractFnReturnType<FnType extends (...args: any) => any> = PromiseValue<ReturnType<FnType>>;

export type QueryConfig<QueryFnType extends (...args: any) => any> = Omit<
  UseQueryOptions<ExtractFnReturnType<QueryFnType>, any>,
  'queryKey' | 'queryFn'
>;

export type MutationConfig<
  MutationFnType extends (...args: any) => any,
  ErrorResponseType = unknown,
> = UseMutationOptions<
  ExtractFnReturnType<MutationFnType>,
  AxiosError<ErrorResponseType>,
  Parameters<MutationFnType>[0]
>;
